"use client"

import type React from "react"
import { useState } from "react"
import { Col, Container, Row, Input, Label, Button } from "reactstrap"
import Breadcrumbs from "../../../CommonElements/Breadcrumbs/Breadcrumbs"
import AnalyticalData from "./analyticaldata"
import type { DateRange, DateRangeAnalytics } from "./types"
import "../../CommonElements/DashboardTheme/dashboard-theme.css"

const AnalyticsContainer: React.FC = () => {
  // Default to last 7 days
  const getDefaultDateRange = (): DateRange => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 7)

    return {
      startDate: start.toISOString().split("T")[0],
      endDate: end.toISOString().split("T")[0],
    }
  }

  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange())
  const [analyticsData, setAnalyticsData] = useState<DateRangeAnalytics | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState(false)

  const handleDateRangeChange = (field: keyof DateRange, value: string) => {
    setDateRange((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleRefresh = () => {
    setRefreshTrigger((prev) => !prev)
  }

  const handleDataLoad = (data: DateRangeAnalytics | null) => {
    setAnalyticsData(data)
  }

  const applyPreset = (days: number) => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - days)
    setDateRange({
      startDate: start.toISOString().split("T")[0],
      endDate: end.toISOString().split("T")[0],
    })
  }

  return (
    <div className="db-page">
      <Breadcrumbs mainTitle="Analytics Dashboard" parent="Dashboard" />
      <Container fluid className="pb-4">
        {/* Date Range Selector */}
        <div className="db-section">
          <div className="db-chart-card">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-1">
              <div className="db-section-head mb-0">
                <span className="db-section-bar db-bg-brand"></span>
                <div>
                  <p className="db-section-eyebrow mb-0">Reporting Window</p>
                  <h3 className="db-section-title">Date Range Selection</h3>
                </div>
              </div>
              <Button size="sm" className="db-btn-brand" onClick={handleRefresh}>
                <i className="fa fa-refresh me-1"></i>
                Refresh
              </Button>
            </div>
            <hr className="db-divider" />
            <Row className="align-items-end g-3">
              <Col md="3">
                <Label for="startDate" className="db-section-eyebrow d-block">
                  Start Date
                </Label>
                <Input
                  type="date"
                  id="startDate"
                  value={dateRange.startDate}
                  onChange={(e) => handleDateRangeChange("startDate", e.target.value)}
                />
              </Col>
              <Col md="3">
                <Label for="endDate" className="db-section-eyebrow d-block">
                  End Date
                </Label>
                <Input
                  type="date"
                  id="endDate"
                  value={dateRange.endDate}
                  onChange={(e) => handleDateRangeChange("endDate", e.target.value)}
                />
              </Col>
              <Col md="3">
                <div className="d-flex gap-2 flex-wrap">
                  <Button size="sm" color="light" className="border" onClick={() => applyPreset(7)}>
                    Last 7 days
                  </Button>
                  <Button size="sm" color="light" className="border" onClick={() => applyPreset(30)}>
                    Last 30 days
                  </Button>
                </div>
              </Col>
              <Col md="3">
                <div className="text-muted" style={{ fontSize: "0.82rem" }}>
                  <i className="fa fa-info-circle me-1"></i>
                  Showing data from {dateRange.startDate} to {dateRange.endDate}
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* Analytics Data Component */}
        <Row>
          <Col md="12">
            <AnalyticalData
              dateRange={dateRange}
              onDataLoad={handleDataLoad}
              key={`${dateRange.startDate}-${dateRange.endDate}-${refreshTrigger}`}
            />
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default AnalyticsContainer
